import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Stack} from "@mui/material";
import { Drawer, Input, SelectField } from "components";
import {
  useAddCustomerVendor,
  useGetAllCountries,
  useGetAllStateByCountryId,
  useUpdateCustomerVendor,
} from "controller";
import { useGetDoesHaveCustomerFullAccess } from "pages/modules/customer/context";
import  { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import * as yup from "yup";

const websiteRegex =
  /^(?:(?:https?|ftp):\/\/)?[A-Za-z0-9-]{1,63}(\.[A-Za-z0-9-]{1,63})*\.[A-Za-z]{2,}$/;
const schema = yup.object({
  vendor_name: yup.string().required("Vendor name is required").trim(),
  contact_person: yup.string().trim(),
  email: yup.string().trim().email("Invalid email format"),
  contact_number: yup
    .string()
    .trim()
    .matches(/^[0-9]*$/, {
      message: "Invalid contact number",
      excludeEmptyString: true,
    })
    .nullable()
    .optional(),
  website: yup
    .string()
    .trim()
    .test(
      "is-empty-or-valid",
      "Please enter a valid website",
      (value) => !value || websiteRegex.test(value),
    )
    .nullable()
    .optional(),
  zip_code: yup
    .string()
    .trim()
    .max(25, "Zip code must be at most 25 characters"),
  street: yup.string().trim(),
  country: yup.string(),
  state: yup.string().nullable().optional(),
  city: yup
    .string()
    .trim()
    .nullable()
    .optional()
    .max(50, "City must be at most 50 characters")
    .matches(/\p{L}/u, {
      message: "City must contain at least one letter",
      excludeEmptyString: true,
    }),
});

function VendorsAddDrawer({
  open,
  onClose,
  drawerData,
}: {
  open: boolean;
  onClose: () => void;
  drawerData?: any;
}) {
  const haveFullAccess = useGetDoesHaveCustomerFullAccess();
  const { id: customerIdParams }: any = useParams();
  const {
    mutateAsync: addCustomerVendor,
    isLoading: isAddCustomerVendorLoading,
  } = useAddCustomerVendor();
  const {
    mutateAsync: updateCustomerVendor,
    isLoading: isUpdateCustomerVendorLoading,
  } = useUpdateCustomerVendor();
  const [drawerStatus, setDrawerStatus] = useState(false);
  const [primaryButtonText, setPrimaryButtonText] = useState("Create");
  const [vendorDrawerTitle, setVendorDrawerTitle] = useState("Add Vendor");

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
    reset,
    setValue,
    watch,
    trigger,
  } = useForm({
    defaultValues: {
      vendor_name: "",
      contact_person: "",
      email: "",
      contact_number: "",
      website: "",
      zip_code: "",
      street: "",
      country: "",
      state: "",
      city: "",
    },
    resolver: yupResolver(schema),
  });

  const handleVendorSave = handleSubmit(async (data: any) => {
    try {
      if (drawerData?.mode == "edit" && drawerData?.data) {
        const vendorId = drawerData?.data?.mapping_id;
        const updateVendors = await updateCustomerVendor({
          vendorId,
          customerId: customerIdParams,
          data,
        });
        if (updateVendors?.mapping_id) {
          toast.success("Vendor updated successfully");
        } else {
          toast.error("Unable to update Vendor at the moment.Try again later");
        }
        onClose();
        reset();
      } else if (drawerData?.mode == "create") {
        const createVendors = await addCustomerVendor({
          customerIdParams,
          data,
        });
        if (createVendors?.mapping_id) {
          toast.success("Vendor created successfully");
        } else {
          toast.error("Unable to Add Vendor at the moment.Try again later");
        }
        onClose();
        reset();
      }
    } catch (error: any) {
      const errorMessage =
        error?.message || "Something went wrong.Try again later";
      toast.error(errorMessage);
    }
  });

  useEffect(() => {
    if (drawerData?.mode == "edit" && drawerData?.data) {
      setVendorDrawerTitle(haveFullAccess ? "Edit Vendor" : "View Vendor");
      setPrimaryButtonText("Save");
      setValue("vendor_name", drawerData?.data?.vendor_name);
      setValue("contact_person", drawerData?.data?.contact_person);
      setValue("email", drawerData?.data?.email);
      setValue("contact_number", drawerData?.data?.contact_number);
      setValue("website", drawerData?.data?.website);
      setValue("zip_code", drawerData?.data?.zip_code);
      setValue("street", drawerData?.data?.street);
      setValue("country", drawerData?.data?.country_id || "");
      setValue("state", drawerData?.data?.state_id || "");
      setValue("city", drawerData?.data?.city);
    } else {
      setVendorDrawerTitle("Add Vendor");
    }
    if (drawerData?.open) {
      setDrawerStatus(true);
    } else {
      setDrawerStatus(false);
      reset();
    }
  }, [drawerData?.open]);

  const watchCountry = watch("country");
  const { data: countries } = useGetAllCountries();
  const { data: provinces } = useGetAllStateByCountryId(watchCountry);
  const [toggleButton, setToggleButton] = useState(false);

  return (
    <Drawer
      title={vendorDrawerTitle}
      drawerStyle={{ width: "404px" }}
      open={drawerStatus}
      onClose={onClose}
      onOpen={() => {}}
      secondaryButtonText="Cancel"
      onSecondaryButtonClick={onClose}
      primaryButtonText={primaryButtonText}
      isPrimaryButtonDisabled={!isDirty}
      isLoading={isAddCustomerVendorLoading || isUpdateCustomerVendorLoading}
      onPrimaryButtonClick={handleVendorSave}
      footer={!haveFullAccess ? <></> : null}
      // footer={vendorDrawerTitle == "edit" ? !isDirty && <></> : ""}
    >
      <Box sx={{ padding: "26px" }}>
        <Stack sx={{ gap: "20px", width: "100%" }}>
          <Stack direction={"row"} sx={{ width: "100%", gap: "16px" }}>
            <Box sx={{ width: "100%" }}>
              <Input
                name={"vendor_name"}
                label="Vendor Name"
                required
                disabled={!haveFullAccess}
                control={control}
                error={errors?.vendor_name ? true : false}
                errorMessage={errors?.vendor_name?.message}
              ></Input>
            </Box>
            <Box sx={{ width: "100%" }}>
              <Input
                name={"contact_person"}
                label="Contact Person"
                control={control}
                disabled={!haveFullAccess}
                errorMessage={errors?.contact_person?.message}
              ></Input>
            </Box>
          </Stack>
          <Stack direction={"row"} sx={{ width: "100%", gap: "16px" }}>
            <Box sx={{ width: "100%" }}>
              <Input
                name={"email"}
                label="Email"
                control={control}
                type="email"
                disabled={!haveFullAccess}
                error={errors?.email ? true : false}
                errorMessage={errors?.email?.message}
              ></Input>
            </Box>
            <Box sx={{ width: "100%" }}>
              <Input
                name={"contact_number"}
                label="Contact Number"
                control={control}
                disabled={!haveFullAccess}
                error={errors?.contact_number ? true : false}
                errorMessage={errors?.contact_number?.message}
              ></Input>
            </Box>
          </Stack>
          <Input
            name={"website"}
            label="Website"
            control={control}
            disabled={!haveFullAccess}
            error={errors?.website ? true : false}
            errorMessage={errors?.website?.message}
          ></Input>
          <Stack direction={"row"} sx={{ width: "100%", gap: "16px" }}>
            <Box sx={{ width: "100%" }}>
              <SelectField
                name={"country"}
                label="Country"
                isSearchable
                keepMenuItemOriginalCasing
                control={control}
                options={countries}
                labelMappingKey="name"
                valueMappingKey="id"
                isResponsive={false}
                disabled={!haveFullAccess}
                size={"small"}
                sx={{ width: "100%" }}
                error={!!errors?.country}
                errorMessage={errors?.country?.message || ""}
              />
            </Box>
          </Stack>
          <Stack direction={"row"} sx={{ width: "100%", gap: "16px" }}>
            <Box sx={{ width: "168px" }}>
              <SelectField
                disabled={!haveFullAccess || !watchCountry}
                name={"state"}
                label="State"
                isSearchable
                keepMenuItemOriginalCasing
                control={control}
                options={provinces}
                labelMappingKey="state_name"
                valueMappingKey="id"
                isResponsive={false}
                size="small"
                sx={{ width: "100%" }}
                error={!!errors?.state}
                errorMessage={errors?.state?.message && ""}
              />
            </Box>
            <Box sx={{ width: "168px" }}>
              <Input
                disabled={!haveFullAccess}
                control={control}
                name={"city"}
                label="City"
                isResponsive={false}
                size="small"
                sx={{ width: "100%" }}
                error={!!errors?.city}
                errorMessage={errors?.city?.message || ""}
                inputProps={{ maxLength: 50 }}
              />
            </Box>
          </Stack>
          <Input
            name={"street"}
            label="Street"
            disabled={!haveFullAccess}
            control={control}
            errorMessage={errors?.street?.message}
          ></Input>
          <Input
            name={"zip_code"}
            label="Zip Code"
            disabled={!haveFullAccess}
            control={control}
            errorMessage={errors?.zip_code?.message}
            inputProps={{ maxLength: 25 }}
          ></Input>
        </Stack>
      </Box>
    </Drawer>
  );
}

export default VendorsAddDrawer;
